import type { CSSProperties } from 'react';

/** Accent behind a "lit" glass surface (active nav tab, primary action). */
export const GLASS_ACCENT = '55,125,255';

// Depth comes from the blur, a hairline light rim and a wide ambient float.
// No dark inset layers: on our light backdrop (#eeeff4) they read as embossed
// plastic rather than glass.
export const GLASS_SHADOW = [
  'inset 0 0 0 1px rgba(255,255,255,0.55)',
  'inset 0 1px 0 0 rgba(255,255,255,0.9)',
  'inset 0 -1px 0 0 rgba(255,255,255,0.3)',
  '0 1px 2px 0 rgba(17,24,39,0.04)',
  '0 10px 24px -10px rgba(17,24,39,0.14)',
  '0 28px 56px -28px rgba(17,24,39,0.22)',
].join(', ');

/** How much backdrop the pane lets through. `thin` for surfaces sitting over
 *  colourful content, `thick` for ones that must stay legible over anything. */
export type GlassTone = 'thin' | 'regular' | 'thick';

// Blur is capped at 24px on purpose: Telegram's WebView has a hard budget for
// stacked backdrop-filter layers (see main.tsx `noglass`), and the denser fill
// already does most of the frosting work.
const TONE: Record<GlassTone, { fill: number; blur: number }> = {
  thin: { fill: 0.42, blur: 16 },
  regular: { fill: 0.58, blur: 20 },
  thick: { fill: 0.72, blur: 24 },
};

/** The shared frosted material (milky fill + wide blur/saturate + rim & float).
 *  Works on iOS WKWebView and Android Chrome — pure CSS, no SVG filter. */
export function glassSurface(tone: GlassTone = 'regular'): CSSProperties {
  const { fill, blur } = TONE[tone];
  const filter = `blur(${blur}px) saturate(180%)`;
  return {
    backgroundColor: `rgba(255,255,255,${fill})`,
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    boxShadow: GLASS_SHADOW,
    isolation: 'isolate',
  };
}

/** Blue bloom for a lit surface — a faint inner tint plus a soft outer halo, so
 *  the pane still reads as glass instead of a painted button. Symmetric so it
 *  doesn't lean into a directional falloff that needs extra room below. */
export function glassGlow(strength = 1): string {
  return [
    `inset 0 0 16px 0 rgba(${GLASS_ACCENT},${0.22 * strength})`,
    `0 0 0 1px rgba(${GLASS_ACCENT},${0.14 * strength})`,
    `0 0 22px 2px rgba(${GLASS_ACCENT},${0.32 * strength})`,
  ].join(', ');
}
